import React, { useState, useEffect } from "react";
import CommonLoader from "../common/CommonLoader";
import SampleBottleSelectorDialog from "../common/SampleBottleSelectorDialog";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  IconButton,
  Divider,
  Typography,
  TableSortLabel,
  Box,
  Button,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  Tooltip,
  Collapse,
} from "@mui/material";
import DeleteConfirmation from "../common/DeleteConfirmation";
import { DeleteOrderedBottle } from "../../services/BottleOrdersService";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ScienceIcon from "@mui/icons-material/Science";
import SaveIcon from "@mui/icons-material/Save";
import ViewOrderedBottle from "./ViewOrderedBottle";
import OrderedBottleDialog from "./OrderedBottleDialog";
import AssignTestsDialog from "../common/AssignTestsDialog";
import AssignSlatesDialog from "../common/AssignSlatesDialog";
import {
  GetOrdersByBottlerOrderNumber,
  AssignLabOrderToOrderedBottle,
  SaveOrderedBottle,
  DuplicateOrderedBottle,
  ReorderOrderedBottles,
} from "../../services/BottleOrdersService";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  getComparator,
  stableSort,
} from "../../../common-utilities/sortingUtils";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { NotificationManager } from "react-notifications";
import { PrintOrderedBottleLabels } from "../../../services/barcodeService";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { GetSampleBottles } from "../../services/SamplesMaintenanceService";
import { UpdateOrderedBottle } from "../../services/BottleOrdersService";

const OrderedBottlesTable = ({
  orderedBottles,
  orderNumber,
  accountNumber,
  onExpandAllChange,
}) => {
  const columns = [
    "Parameter",
    "Quantity",
    "Bottle Name",
    "Location",
    "CofC Description",
    "Monitor Dates",
    "Lab Order",
    "Actions",
  ];

  const columnHeaders = [
    "parameter",
    "quantity",
    "sampleBottleName",
    "waterSystemFacilityName",
    "cocNum",
    "monitorDate",
    "labOrderNumber",
  ];

  const [bottles, setBottles] = useState(orderedBottles || []);
  const [page, setPage] = useState(0);
  // recordids of the currently expanded rows
  const [expandedRowIds, setExpandedRowIds] = useState(new Set());
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("asc");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBottle, setEditingBottle] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedBottleToDelete, setSelectedBottleToDelete] = useState(null);
  const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableOrders, setAvailableOrders] = useState([]);
  const [assignTestsDialogOpen, setAssignTestsDialogOpen] = useState(false);
  const [slateDialogOpen, setSlateDialogOpen] = useState(false);
  const [sampleBottles, setSampleBottles] = useState([]);
  const [sampleDialogOpen, setSampleDialogOpen] = useState(false);
  const [selectedBottle, setSelectedBottle] = useState(null);

  // Tracks unsaved lab order changes: { [recordid]: labOrderNumber }
  const [pendingLabOrders, setPendingLabOrders] = useState({});

  useEffect(() => {
    fetchAvailableOrders();
    fetchSampleBottles();
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Toggles the expanded "Expanded View" row nested under the clicked bottle row.
  const handleViewClick = (bottle) => {
    setExpandedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(bottle.recordid)) {
        next.delete(bottle.recordid);
      } else {
        next.add(bottle.recordid);
      }
      return next;
    });
  };

  // Expands every ordered bottle if any are collapsed, otherwise collapses them all.
  // Also tells the parent so the bottle order sections below can expand/collapse with it.
  const handleToggleExpandAll = () => {
    const expandAll = expandedRowIds.size !== sortedData.length;
    setExpandedRowIds(
      expandAll ? new Set(sortedData.map((bottle) => bottle.recordid)) : new Set()
    );
    onExpandAllChange?.(expandAll);
  };

  const handleSampleBottleSelect = async (selected) => {
    if (!selectedBottle || !selected) return;

    await handleSampleBottleUpdate(selectedBottle, selected.sampleBottleName);
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const fetchSampleBottles = async () => {
    try {
      const response = await GetSampleBottles();

      setSampleBottles(
        (response?.results || []).map((x) => ({
          ...x,
          sampleBottleName: x.sampleBottleName?.trim() || "",
        }))
      );
    } catch (error) {
      NotificationManager.error("Failed to load sample bottles");
    }
  };

  const handleSampleBottleUpdate = async (bottle, sampleBottleName) => {
    const trimmedBottleName = sampleBottleName?.trim() || "";

    const updatedBottle = {
      ...bottle,
      sampleBottleName: trimmedBottleName,
    };

    try {
      await UpdateOrderedBottle(updatedBottle);

      setBottles((prev) =>
        prev.map((item) =>
          item.recordid === bottle.recordid
            ? {
              ...item,
              sampleBottleName: trimmedBottleName,
            }
            : item
        )
      );

      NotificationManager.success("Sample bottle updated successfully");
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to update sample bottle"
      );
    }
  };

  // Saves a single field edited inline in the expanded view via the same
  // update endpoint the "Sample Bottle Name" quick-edit already uses.
  const handleOrderedBottleFieldSave = async (bottle, field, value) => {
    const updatedBottle = { ...bottle, [field]: value };

    try {
      await UpdateOrderedBottle(updatedBottle);

      setBottles((prev) =>
        prev.map((item) =>
          item.recordid === bottle.recordid ? updatedBottle : item
        )
      );
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to update ordered bottle"
      );
      throw error;
    }
  };

  const handleBottleLabelsPrint = async (orderNumber) => {
    try {
      const response = await PrintOrderedBottleLabels(
        "sampleLabel",
        orderNumber
      );
      NotificationManager.success("Bottle ordered label printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle ordered");
    }
  };

  const handleBottleBarCodePrint = async (orderNumber) => {
    try {
      const response = await PrintOrderedBottleLabels(
        "sampleBarcode",
        orderNumber
      );
      NotificationManager.success("Bottle ordered label printed successfully");
    } catch (error) {
      NotificationManager.error("Error printing bottle ordered");
    }
  };

  const formatMonitorDate = (start, end) => {
    // Date-only values ("YYYY-MM-DD") from the edit dialog would parse as UTC
    // and show a day early, so drop any time part and build a local date
    const format = (d) => {
      if (!d) return "";
      const [y, m, day] = String(d).split("T")[0].split("-").map(Number);
      return new Date(y, m - 1, day).toLocaleDateString("en-US");
    };

    const s = format(start);
    const e = format(end);

    if (!s && !e) return "";
    return `${s} - ${e}`;
  };

  const handleAddNew = () => {
    if (bottles && bottles.length > 0) {
      const templateBottle = { ...bottles[bottles.length - 1] };
      templateBottle.recordid = 0;
      templateBottle.quantity = 0;
      templateBottle.cocNum = "";
      templateBottle.identifier = "";
      setEditingBottle(templateBottle);
    } else {
      setEditingBottle(null);
    }
    setDialogOpen(true);
  };

  const handleEdit = (bottle) => {
    setEditingBottle(bottle);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingBottle(null);
  };

  const renderWithTooltip = (text, maxLength = 20) => {
    if (!text) return "-";

    const stringText = text.toString();
    const isLong = stringText.length > maxLength;

    const shortText = isLong
      ? `${stringText.slice(0, maxLength)}...`
      : stringText;

    return (
      <Tooltip title={stringText}>
        <span style={{ cursor: "pointer" }}>
          {shortText}
        </span>
      </Tooltip>
    );
  };

  const handleSave = (bottleData) => {
    if (editingBottle && editingBottle.recordid !== 0) {
      const updatedBottles = bottles.map((bottle) =>
        bottle.recordid === editingBottle.recordid ? { ...bottleData } : bottle
      );
      setBottles(updatedBottles);
    } else {
      const newBottle = {
        ...bottleData,
        recordid: Math.max(0, ...bottles.map((b) => b.recordid || 0)) + 1,
      };
      setBottles([...bottles, newBottle]);
    }
  };

  // Copies the line on the server and shows the copy directly below the source
  const handleDuplicate = async (bottle) => {
    setLoading(true);
    try {
      const response = await DuplicateOrderedBottle(bottle.recordid);
      const copy = response?.results;
      if (!copy) throw new Error("Failed to duplicate ordered bottle");

      setBottles((prev) => {
        const next = [...prev];
        const sourceIndex = next.findIndex((b) => b.recordid === bottle.recordid);
        next.splice(sourceIndex + 1, 0, copy);
        return next;
      });
      NotificationManager.success("Ordered bottle duplicated");
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to duplicate ordered bottle"
      );
    } finally {
      setLoading(false);
    }
  };

  // Drag-and-drop only works on the saved order, so it is off while a column sort or search is active
  const canReorder = !orderBy && !searchTerm.trim();

  const handleDragEnd = async (result) => {
    const { source, destination } = result;
    if (!destination || source.index === destination.index) return;

    // Draggable indexes are page-relative; move within the full list
    const offset = page * rowsPerPage;
    const previous = bottles;
    const next = [...bottles];
    const [moved] = next.splice(offset + source.index, 1);
    next.splice(offset + destination.index, 0, moved);
    setBottles(next);

    try {
      await ReorderOrderedBottles(
        orderNumber,
        next.map((b) => b.recordid)
      );
    } catch (error) {
      setBottles(previous);
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to save line order"
      );
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const getFilteredBottles = () => {
    if (!searchTerm || searchTerm.trim() === "") {
      return bottles;
    }

    const lowerSearchTerm = searchTerm.toLowerCase().trim();

    return bottles.filter((bottle) => {
      const searchableFields = [
        bottle.orderNumber?.toString() || "",
        bottle.parameter || "",
        bottle.sampleBottleName || "",
        bottle.quantity?.toString() || "",
        bottle.waterSystemFacilityName || "",
        bottle.preservatives || "",
        bottle.cocNum || "",
        bottle.sampleType || "",
        bottle.identifier || "",
        bottle.analyteCode || "",
      ];

      return searchableFields.some((field) =>
        field.toLowerCase().includes(lowerSearchTerm)
      );
    });
  };

  const handleDeleteClick = (recordid) => {
    setSelectedBottleToDelete(recordid);
    setIsDeleteConfirmationOpen(true);
  };

  const handleDeleteConfirmationClose = () => {
    setIsDeleteConfirmationOpen(false);
  };

  const handleDeleteConfirmationConfirm = async () => {
    setLoading(true);
    try {
      const response = await DeleteOrderedBottle(selectedBottleToDelete);
      if (response.results === true) {
        NotificationManager.success("Ordered bottle deleted successfully");
        setIsDeleteConfirmationOpen(false);
        setLoading(false);
        setBottles((prev) =>
          prev.filter((bottle) => bottle.recordid !== selectedBottleToDelete)
        );
        // Collapse the row if the deleted bottle was expanded
        setExpandedRowIds((prev) => {
          if (!prev.has(selectedBottleToDelete)) return prev;
          const next = new Set(prev);
          next.delete(selectedBottleToDelete);
          return next;
        });
      } else {
        NotificationManager.error("Error deleting ordered bottle");
        setLoading(false);
      }
    } catch (error) {
      NotificationManager.error("Error deleting ordered bottle");
      setLoading(false);
    }
  };

  const fetchAvailableOrders = async () => {
    setLoading(true);
    try {
      const response = await GetOrdersByBottlerOrderNumber(orderNumber);

      const sortedOrders = (response?.results || []).sort(
        (a, b) => Number(a.orderNumber) - Number(b.orderNumber)
      );

      setAvailableOrders(sortedOrders);
    } catch (error) {
      NotificationManager.error(
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch available orders."
      );
      setAvailableOrders([]);
    } finally {
      setLoading(false);
    }
  };

  // Stage the change locally — does NOT call the API
  const handleUpdateLabOrder = (recordid, newOrderNo) => {
    const labOrderNumber = parseInt(newOrderNo, 10) || 0;
    setPendingLabOrders((prev) => ({
      ...prev,
      [recordid]: labOrderNumber,
    }));
  };

  // Flush all pending lab order changes to the API
  const handleSaveLabOrders = async () => {
    const entries = Object.entries(pendingLabOrders);
    if (entries.length === 0) return;

    setLoading(true);
    const failedKeys = [];

    for (const [recordidStr, labOrderNumber] of entries) {
      const recordid = parseInt(recordidStr, 10);
      const bottle = bottles.find((b) => b.recordid === recordid);
      if (!bottle) continue;

      try {
        await AssignLabOrderToOrderedBottle(
          labOrderNumber,
          bottle.orderNumber,
          bottle.recordid
        );

        // Commit to local bottles state on success
        setBottles((prev) =>
          prev.map((b) =>
            b.recordid === recordid
              ? { ...b, ordersOrderNumber: labOrderNumber }
              : b
          )
        );
      } catch (err) {
        failedKeys.push(recordidStr);
        NotificationManager.error(
          err?.response?.data?.message ||
          err?.message ||
          `Failed to assign lab order for bottle ${recordid}`
        );
      }
    }

    // Clear only successfully saved entries from pending
    setPendingLabOrders((prev) => {
      const remaining = { ...prev };
      Object.keys(remaining).forEach((key) => {
        if (!failedKeys.includes(key)) delete remaining[key];
      });
      return remaining;
    });

    if (failedKeys.length === 0) {
      NotificationManager.success("Lab orders saved successfully");
    }

    setLoading(false);
  };

  const handleAssignTest = () => {
    setAssignTestsDialogOpen(true);
  };

  const handleCloseAssignTestsDialog = () => {
    setAssignTestsDialogOpen(false);
  };

  const handleAssignSlate = () => {
    setSlateDialogOpen(true);
  };

  const handleCloseSlateDialog = () => {
    setSlateDialogOpen(false);
  };

  const handleAssignTestsComplete = (result) => {
    setAssignTestsDialogOpen(false);
    NotificationManager.success("Tests assigned to bottle order successfully");
    window.location.reload();
  };

  const handleSlateAssignComplete = (result) => {
    setSlateDialogOpen(false);
    NotificationManager.success("Slate assigned to bottle order successfully");
    window.location.reload();
  };

  const renderActions = (sampleData) => {
    const isExpanded = expandedRowIds.has(sampleData.recordid);

    return (
      <div style={{ minWidth: "240px" }}>
        <IconButton
          aria-label="print label"
          style={{ color: "#0eecf0" }}
          onClick={() => handleBottleLabelsPrint(sampleData.recordid)}
        >
          <LocalPrintshopIcon />
        </IconButton>
        <IconButton
          aria-label="print barcode"
          style={{ color: "#8911f2" }}
          onClick={() => handleBottleBarCodePrint(sampleData.recordid)}
        >
          <QrCodeIcon />
        </IconButton>
        <IconButton
          aria-label="edit"
          style={{ color: "orange" }}
          onClick={() => handleEdit(sampleData)}
        >
          <EditIcon />
        </IconButton>
        <Tooltip title="Duplicate">
          <IconButton
            aria-label="duplicate"
            style={{ color: "#1976d2" }}
            onClick={() => handleDuplicate(sampleData)}
          >
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={isExpanded ? "Hide expanded view" : "Expanded view"}>
          <IconButton
            aria-label="view"
            style={{ color: "green" }}
            onClick={() => handleViewClick(sampleData)}
          >
            {isExpanded ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        </Tooltip>
        <IconButton
          aria-label="delete"
          style={{ color: "red" }}
          onClick={() => handleDeleteClick(sampleData.recordid)}
        >
          <DeleteIcon />
        </IconButton>
      </div>
    );
  };

  const hasPendingChanges = Object.keys(pendingLabOrders).length > 0;
  const filteredBottles = getFilteredBottles();
  const sortedData = stableSort(filteredBottles, getComparator(order, orderBy));
  // +1 for the drag handle column
  const totalColumnCount = columns.length + 1;

  return (
    <Paper elevation={3} sx={{ padding: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography variant="h5">Ordered Bottles</Typography>
          
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            flex: 1,
            justifyContent: "flex-end",
          }}
        >
          <TextField
            size="small"
            placeholder="Search bottles..."
            value={searchTerm}
            onChange={handleSearchChange}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={
              expandedRowIds.size === sortedData.length && sortedData.length > 0 ? (
                <VisibilityOffIcon />
              ) : (
                <VisibilityIcon />
              )
            }
            onClick={handleToggleExpandAll}
            disabled={sortedData.length === 0}
          >
            {expandedRowIds.size === sortedData.length && sortedData.length > 0
              ? "Collapse"
              : "Expand"}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AssignmentIcon />}
            onClick={handleAssignTest}
            size="small"
          >
            Assign Test
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<ScienceIcon />}
            onClick={handleAssignSlate}
            size="small"
          >
            Assign Slate
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddNew}
          >
            New
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<SaveIcon />}
            onClick={handleSaveLabOrders}
            disabled={loading || !hasPendingChanges}
            sx={{
              ...(hasPendingChanges && {
                animation: "pulse 1.5s ease-in-out infinite",
                "@keyframes pulse": {
                  "0%": { boxShadow: "0 0 0 0 rgba(46, 125, 50, 0.5)" },
                  "70%": { boxShadow: "0 0 0 8px rgba(46, 125, 50, 0)" },
                  "100%": { boxShadow: "0 0 0 0 rgba(46, 125, 50, 0)" },
                },
              }),
            }}
          >
            Save
            {hasPendingChanges && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  backgroundColor: "#fff",
                  color: "success.main",
                  borderRadius: "50%",
                  width: 20,
                  height: 20,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.7rem",
                  fontWeight: "bold",
                }}
              >
                {Object.keys(pendingLabOrders).length}
              </Box>
            )}
          </Button>
        </Box>
      </Box>

      {/* Unsaved changes warning banner */}
      {hasPendingChanges && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1,
            backgroundColor: "#fff8e1",
            border: "1px solid #f59e0b",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 500 }}>
            ⚠ You have {Object.keys(pendingLabOrders).length} unsaved lab order{" "}
            {Object.keys(pendingLabOrders).length === 1 ? "change" : "changes"}.
            Click <strong>Save Changes</strong> to commit.
          </Typography>
        </Box>
      )}

      <Divider sx={{ mb: 2 }} />
      <TableContainer>
        <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="ordered-bottles">
        {(droppableProvided) => (
        <Table
          sx={{ minWidth: 400 }}
          size="small"
          dense
          ref={droppableProvided.innerRef}
          {...droppableProvided.droppableProps}
        >
          <TableHead
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: "white",
            }}
          >
            <TableRow>
              <TableCell sx={{ width: 32, px: 0.5 }}>
                <Tooltip
                  title={
                    orderBy
                      ? "Click to clear the column sort and drag lines again"
                      : "Drag lines to rearrange"
                  }
                >
                  <DragIndicatorIcon
                    fontSize="small"
                    onClick={() => setOrderBy("")}
                    sx={{ cursor: orderBy ? "pointer" : "default", verticalAlign: "middle" }}
                  />
                </Tooltip>
              </TableCell>
              {columns.map((column, index) => (
                <TableCell
                  key={column}
                  style={{
                    cursor:
                      index < columnHeaders.length ? "pointer" : "default",
                  }}
                >
                  {index < columnHeaders.length ? (
                    <TableSortLabel
                      active={orderBy === columnHeaders[index]}
                      direction={
                        orderBy === columnHeaders[index] ? order : "asc"
                      }
                      onClick={() =>
                        handleRequestSort(columnHeaders[index])
                      }
                    >
                      {column}
                    </TableSortLabel>
                  ) : (
                    column
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
            {sortedData.length > 0 ? (
              sortedData
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((bottle, index) => {
                  const isExpanded = expandedRowIds.has(bottle.recordid);

                  return (
                    // Each bottle is its own <tbody> (normal row + nested "Expanded View" row)
                    // so the expanded view moves with its line while dragging.
                    <Draggable
                      key={bottle.recordid}
                      draggableId={bottle.recordid.toString()}
                      index={index}
                      isDragDisabled={!canReorder}
                    >
                    {(draggableProvided, snapshot) => (
                    <TableBody
                      ref={draggableProvided.innerRef}
                      {...draggableProvided.draggableProps}
                      style={{
                        ...draggableProvided.draggableProps.style,
                        ...(snapshot.isDragging && {
                          display: "table",
                          backgroundColor: "white",
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.2)",
                        }),
                      }}
                    >
                      <TableRow
                        sx={{
                          backgroundColor: isExpanded
                            ? "rgba(25, 118, 210, 0.08)"
                            : index % 2 === 0
                              ? "rgba(0, 0, 0, 0.04)"
                              : "white",
                        }}
                      >
                        <TableCell
                          {...draggableProvided.dragHandleProps}
                          sx={{
                            width: 32,
                            px: 0.5,
                            cursor: canReorder ? "grab" : "not-allowed",
                            color: canReorder ? "text.secondary" : "text.disabled",
                          }}
                        >
                          <DragIndicatorIcon fontSize="small" />
                        </TableCell>
                        {columnHeaders.map((column) => {
                          if (column === "sampleBottleName") {
                            return (
                              <TableCell
                                key={column}
                                onClick={() => {
                                  setSelectedBottle(bottle);
                                  setSampleDialogOpen(true);
                                }}
                                sx={{
                                  cursor: "pointer",
                                  "&:hover": { color: "#2290e9" },
                                }}
                              >
                                {bottle.sampleBottleName || (
                                  <Typography color="text.secondary">
                                    Click to select
                                  </Typography>
                                )}
                              </TableCell>
                            );
                          }

                          if (column === "labOrderNumber") {
                            const isPending =
                              pendingLabOrders.hasOwnProperty(bottle.recordid);

                            const currentValue = isPending
                              ? pendingLabOrders[bottle.recordid]
                              : bottle.ordersOrderNumber ?? "";

                            return (
                              <TableCell key={column}>
                                <Select
                                  size="small"
                                  value={currentValue}
                                  onChange={(e) =>
                                    handleUpdateLabOrder(
                                      bottle.recordid,
                                      e.target.value
                                    )
                                  }
                                  disabled={loading}
                                  sx={{
                                    minWidth: 150,
                                    ...(isPending && {
                                      backgroundColor: "#fff8e1",
                                      "& .MuiOutlinedInput-notchedOutline": {
                                        borderColor: "#f59e0b",
                                        borderWidth: "2px",
                                      },
                                    }),
                                  }}
                                >
                                  <MenuItem value="">
                                    <em>None</em>
                                  </MenuItem>

                                  {availableOrders.map((order) => (
                                    <MenuItem
                                      key={order.orderNumber}
                                      value={order.orderNumber}
                                    >
                                      {order.orderNumber}
                                    </MenuItem>
                                  ))}
                                </Select>
                              </TableCell>
                            );
                          }

                          if (column === "monitorDate") {
                            return (
                              <TableCell key={column}>
                                {formatMonitorDate(
                                  bottle.monitorStart,
                                  bottle.monitorEnd
                                )}
                              </TableCell>
                            );
                          }

                          return (
                            <TableCell key={column}>
                              {column === "parameter" ||
                                column === "waterSystemFacilityName" ||
                                column === "cocNum"
                                ? renderWithTooltip(bottle[column])
                                : bottle[column]}
                            </TableCell>
                          );
                        })}

                        <TableCell>{renderActions(bottle)}</TableCell>
                      </TableRow>

                      {/* Nested "Expanded View" row - only rendered/mounted while relevant to keep the DOM light */}
                      <TableRow>
                        <TableCell
                          colSpan={totalColumnCount}
                          sx={{ py: 0, borderBottom: isExpanded ? undefined : "none" }}
                        >
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ my: 1 }}>
                              <ViewOrderedBottle
                                orderedBottleData={bottle}
                                onFieldSave={handleOrderedBottleFieldSave}
                                sampleBottles={sampleBottles}
                              />
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                    )}
                    </Draggable>
                  );
                })
            ) : (
              <TableBody>
              <TableRow>
                <TableCell
                  colSpan={totalColumnCount}
                  align="center"
                  sx={{ py: 3 }}
                >
                  <Typography variant="body2" color="text.secondary">
                    {searchTerm
                      ? "No bottles found matching your search"
                      : "No ordered bottles available"}
                  </Typography>
                </TableCell>
              </TableRow>
              </TableBody>
            )}
          {droppableProvided.placeholder}
        </Table>
        )}
        </Droppable>
        </DragDropContext>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 200]}
        component="div"
        count={filteredBottles.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <OrderedBottleDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleSave}
        bottleData={editingBottle}
        orderNumber={orderNumber}
        accountNumber={accountNumber}
      />

      <AssignTestsDialog
        open={assignTestsDialogOpen}
        onClose={handleCloseAssignTestsDialog}
        itemType="Order"
        itemLabel="Order:"
        itemValue={orderNumber.toString()}
        matrix="All"
        onAssignComplete={handleAssignTestsComplete}
      />

      <AssignSlatesDialog
        open={slateDialogOpen}
        onClose={handleCloseSlateDialog}
        mode="BOTTLEORDER"
        orderNumber={orderNumber}
        accountNumber={accountNumber}
        onAssignComplete={handleSlateAssignComplete}
      />

      <DeleteConfirmation
        isOpen={isDeleteConfirmationOpen}
        onClose={handleDeleteConfirmationClose}
        onConfirm={handleDeleteConfirmationConfirm}
        title="Delete Confirmation"
        message="Are you sure you want to delete this ordered bottle?"
        color="red"
      />

      <CommonLoader loading={loading} />
      <SampleBottleSelectorDialog
        open={sampleDialogOpen}
        onClose={() => setSampleDialogOpen(false)}
        sampleBottles={sampleBottles}
        loading={loading}
        onSelect={handleSampleBottleSelect}
      />
    </Paper>
  );
};

export default OrderedBottlesTable;